import { createSimpleContext } from "@opencode/ui/context"
import { createEffect, onCleanup, untrack } from "solid-js"
import { Persist, persisted } from "@/runtime/persistence/storage"
import { ServerConnection } from "@/runtime/server/registry"
import { useGlobal } from "@/runtime/server/runtime"
import { readSessionTabsRemovedDetail, SESSION_TABS_REMOVED_EVENT } from "@/shell/titlebar/session-events"
import { initialSidebarState, SidebarStateSchema } from "./schema"
import { createSidebarState } from "./state"

export const { use: useSidebar, provider: SidebarProvider } = createSimpleContext({
  name: "Sidebar",
  gate: true,
  init: () => {
    const global = useGlobal()
    const [state, setState, , ready] = persisted(
      { ...Persist.global("sair-sidebar"), sync: "global" },
      SidebarStateSchema,
      initialSidebarState(),
    )
    const sidebar = createSidebarState(state, setState)
    const knownServers = new Set<ServerConnection.Key>()
    createEffect(() => {
      if (!ready()) return
      const servers = global.servers.list()
      const keys = new Set(servers.map(ServerConnection.key))
      // A server absent during startup may still be restoring. Only an observed removal cleans pins.
      for (const key of knownServers) {
        if (keys.has(key)) continue
        // Managed connections disappear on sign-out or release; their account-scoped state must survive.
        if (!key.startsWith("remote:")) untrack(() => sidebar.removeServer(key))
        knownServers.delete(key)
      }
      for (const conn of servers) {
        const key = ServerConnection.key(conn)
        knownServers.add(key)
        const ctx = global.ensureServerCtx(conn)
        onCleanup(ctx.data.on("session.deleted", (event) => sidebar.removeSessions(key, [event.data.sessionID])))
        createEffect(() => {
          const closed = ctx.projects.closed()
          untrack(() =>
            closed.forEach((directory) => sidebar.removeProject({ type: "project", server: key, directory })),
          )
        })
      }
    })
    const removed = (event: Event) => {
      const detail = readSessionTabsRemovedDetail(event)
      if (detail) sidebar.removeSessions(detail.server, detail.sessionIDs)
    }
    window.addEventListener(SESSION_TABS_REMOVED_EVENT, removed)
    onCleanup(() => window.removeEventListener(SESSION_TABS_REMOVED_EVENT, removed))
    return { ...sidebar, ready }
  },
})
